"use client";

import { useContext, useRef } from "react";

import { LayoutRouterContext } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { usePathname } from "next/navigation";

import { AnimatePresence, motion } from "motion/react";

// Based on the answer at https://stackoverflow.com/a/77715364

function FrozenRouter({ children }: { children: React.ReactNode }) {
  const context = useContext(LayoutRouterContext);
  const frozen = useRef(context).current;
  return (
    <LayoutRouterContext.Provider value={frozen}>
      {children}
    </LayoutRouterContext.Provider>
  );
}

const variants = {
  initial: { opacity: 0, y: 50 },
  enter: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -100 },
};

interface PageTransitionProps {
  excludedPaths?: string[];
  children: React.ReactNode;
}

function PageTransition({ excludedPaths, children }: PageTransitionProps) {
  const pathname = usePathname();
  const isExcludedPath = excludedPaths?.some((path) =>
    pathname.startsWith(path),
  );

  return (
    <AnimatePresence
      mode="wait"
      onExitComplete={() => {
        window.scrollTo(0, 0);
      }}
    >
      <motion.div
        key={pathname}
        initial={!isExcludedPath ? "initial" : false}
        animate={!isExcludedPath ? "enter" : false}
        exit={!isExcludedPath ? "exit" : undefined}
        variants={variants}
        transition={{ ease: "easeInOut" }}
        className="relative"
      >
        <FrozenRouter>{children}</FrozenRouter>
      </motion.div>
    </AnimatePresence>
  );
}

export { PageTransition };
